import { createFileRoute, useNavigate, redirect } from "@tanstack/react-router";
import { useEffect, useState } from "react";
const getSupabase = () => import("@/integrations/supabase/client").then((m) => m.supabase);
import { lovable } from "@/integrations/lovable";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "sonner";
import { Eyebrow, DisplayHeading } from "@/components/editorial";

export const Route = createFileRoute("/login")({
  validateSearch: (
    search: Record<string, unknown>,
  ): { tab?: "signup" | "signin" | "magic" } => ({
    tab:
      search.tab === "signup" || search.tab === "signin" || search.tab === "magic"
        ? search.tab
        : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Sign in — Tiger's Eye" },
      { name: "description", content: "Sign in to Tiger's Eye to start an impartial academic draft review." },
      { name: "robots", content: "noindex,follow" },
      { property: "og:title", content: "Sign in — Tiger's Eye" },
      { property: "og:description", content: "Sign in to Tiger's Eye Academic Review." },
      { property: "og:url", content: "https://tigerseye.app/login" },
    ],
    links: [{ rel: "canonical", href: "https://tigerseye.app/login" }],
  }),
  component: LoginPage,
});

// Use a stable canonical origin so magic-link / OAuth redirects land on the
// same host the email was generated for. Avoids "One-time token not found"
// when a user requests from one origin (e.g. preview) and opens the email
// on another (e.g. tigerseye.app).
function canonicalOrigin() {
  if (typeof window === "undefined") return "https://tigerseye.app";
  const host = window.location.hostname;
  if (host === "tigerseye.app" || host === "www.tigerseye.app") {
    return "https://tigerseye.app";
  }
  if (host === "tigerseye.lovable.app") {
    return "https://tigerseye.lovable.app";
  }
  return window.location.origin;
}

function LoginPage() {
  const navigate = useNavigate();
  const { tab } = Route.useSearch();
  const { user, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate({ to: "/dashboard" });
  }, [user, loading, navigate]);

  const handleGoogle = async () => {
    const result = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: `${canonicalOrigin()}/auth/callback`,
    });
    if (result.error) {
      toast.error(result.error.message ?? "Google sign-in failed");
      return;
    }
    if (result.redirected) return;
    navigate({ to: "/auth/callback" });
  };

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    const supabase = await getSupabase();
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setSubmitting(false);
    if (error) return toast.error(error.message);
    // Route through /auth/callback so new users land on /profile and
    // returning users land on /dashboard via the same logic as magic-link.
    navigate({ to: "/auth/callback" });
  };


  const handleEmailSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    const redirectTo = `${canonicalOrigin()}/auth/callback`;
    const supabase = await getSupabase();
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectTo,
        data: { display_name: displayName || email.split("@")[0] },
      },
    });
    setSubmitting(false);
    if (error) return toast.error(error.message);
    toast.success("Check your email to verify your account.");
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 mx-auto max-w-md w-full px-6 py-16">
        <Eyebrow className="mb-3">Account</Eyebrow>
        <DisplayHeading level={1} className="mb-6">Sign in to Tiger's Eye</DisplayHeading>

        <Button onClick={handleGoogle} variant="outline" className="w-full mb-6">
          Continue with Google
        </Button>

        <div className="relative my-6 text-center">
          <span className="bg-background px-3 text-xs text-ink-tertiary relative z-10">or</span>
          <span className="absolute inset-x-0 top-1/2 border-t border-border" />
        </div>

        <Tabs defaultValue={tab ?? "magic"}>
          <TabsList className="w-full grid grid-cols-3">
            <TabsTrigger value="magic">Magic link</TabsTrigger>
            <TabsTrigger value="signin">Sign in</TabsTrigger>
            <TabsTrigger value="signup">Create account</TabsTrigger>
          </TabsList>
          <TabsContent value="magic">
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                setSubmitting(true);
                const supabase = await getSupabase();
                const { error } = await supabase.auth.signInWithOtp({
                  email,
                  options: { emailRedirectTo: `${canonicalOrigin()}/auth/callback` },
                });
                setSubmitting(false);
                if (error) return toast.error(error.message);
                toast.success("Check your email for a sign-in link.");
              }}
              className="space-y-4 pt-4"
            >
              <div>
                <Label htmlFor="magic-email">Email</Label>
                <Input id="magic-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <Button type="submit" className="w-full" disabled={submitting}>
                Send magic link
              </Button>
              <p className="text-xs text-ink-tertiary text-center">
                We'll email you a one-tap sign-in link. No password needed.
              </p>
            </form>
          </TabsContent>
          <TabsContent value="signin">
            <form onSubmit={handleEmailSignIn} className="space-y-4 pt-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} />
              </div>
              <Button type="submit" className="w-full" disabled={submitting}>Sign in</Button>
            </form>
          </TabsContent>
          <TabsContent value="signup">
            <form onSubmit={handleEmailSignUp} className="space-y-4 pt-4">
              <div>
                <Label htmlFor="name">Display name</Label>
                <Input id="name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="email2">Email</Label>
                <Input id="email2" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="password2">Password</Label>
                <Input id="password2" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} />
              </div>
              <Button type="submit" className="w-full" disabled={submitting}>Create account</Button>
            </form>
          </TabsContent>
        </Tabs>
      </main>
      <Footer />
    </div>
  );
}
